// VideoRoom.jsx (doctor)
// Full-screen video call. Rendered when the doctor admits a patient.

import {
  VideoTileGrid,
  ControlBar,
  AudioInputControl,
  VideoInputControl,
  AudioOutputControl,
  ContentShareControl,
  useMeetingStatus,
  MeetingStatus,
  useLocalVideo,
} from "amazon-chime-sdk-component-library-react";
import { useState, useEffect } from "react";
import Spinner from "./Spinner.jsx";

export default function VideoRoom({ session, connecting, onLeave }) {
  const meetingStatus                   = useMeetingStatus();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const [leaving, setLeaving]           = useState(false);

  // Auto-enable camera when the call is live
  useEffect(() => {
    if (meetingStatus === MeetingStatus.Succeeded && !isVideoEnabled) {
      toggleVideo();
    }
  }, [meetingStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLeave = async () => {
    setLeaving(true);
    await onLeave();
  };

  // Still establishing the Chime session
  if (connecting || meetingStatus === MeetingStatus.Loading) {
    return (
      <div className="video-room video-room--loading">
        <Spinner message={`Connecting to ${session?.patientName ?? "patient"}…`} />
      </div>
    );
  }

  if (meetingStatus === MeetingStatus.Failed) {
    return (
      <div className="video-room video-room--error">
        <div className="video-error-card">
          <span aria-hidden="true">📡</span>
          <h2>Connection failed</h2>
          <p>Could not connect to the video call. Please try again.</p>
          <button className="btn btn-primary" onClick={onLeave}>Back to queue</button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-room">

      {/* Header bar */}
      <div className="video-header" role="banner">
        <div className="video-logo">KSM <span>CareLink</span></div>

        <div className="video-session-info">
          <span className="session-label">In visit with</span>
          <span className="session-patient">{session?.patientName ?? "Patient"}</span>
          {session?.appointmentTime && (
            <span className="session-time">
              {new Date(session.appointmentTime).toLocaleTimeString("en-US", {
                hour: "2-digit", minute: "2-digit",
              })}
            </span>
          )}
        </div>

        <div className="video-live-badge" aria-label="Live call in progress">
          <span className="live-dot" aria-hidden="true" /> LIVE
        </div>
      </div>

      {/* Video grid */}
      <div className="video-grid" role="main" aria-label="Video call">
        <VideoTileGrid
          className="tile-grid"
          noRemoteVideoView={
            <div className="no-remote-video" role="status">
              <div className="no-remote-avatar" aria-hidden="true">
                {session?.patientName?.[0]?.toUpperCase() ?? "P"}
              </div>
              <p>{session?.patientName ?? "Patient"} is connected — waiting for video…</p>
            </div>
          }
        />
      </div>

      {/* Controls */}
      <div className="video-controls" role="toolbar" aria-label="Call controls">
        <ControlBar layout="undocked-horizontal" showLabels>
          <AudioInputControl />
          <VideoInputControl />
          <AudioOutputControl />
          <ContentShareControl />
        </ControlBar>

        <button
          className="btn btn-end-call"
          onClick={handleLeave}
          disabled={leaving}
          aria-label="End video visit"
        >
          {leaving ? "Ending…" : "End Visit"}
        </button>
      </div>

    </div>
  );
}
